/**
 * Persists PdfViewer route fields (without File objects) so a full page reload on /pdf-viewer
 * can restore detail, role, etc. In manual mode the PDF bytes come from IndexedDB
 * (loadSavedPdf). Local-server / Cloud DB flows always fetch PDF on demand.
 */

import { Language } from '../constants/strings';

export const SESSION_PDF_VIEWER_META_KEY = 'qdPdfViewerMeta';

export type PdfViewerPersistedMeta = {
    detail?: string;
    order?: string;
    density?: string;
    counter?: string;
    role?: 'user' | 'editor';
    engineerName?: string;
    revision?: string;
    reportLanguage?: Language;
};

export function persistPdfViewerMetaFromState(state: Record<string, unknown> | null | undefined): void {
    if (!state || !(state[ 'pdfFile' ] instanceof File)) return;
    try {
        const payload: PdfViewerPersistedMeta = {
            detail: state[ 'detail' ] as string | undefined,
            order: state[ 'order' ] as string | undefined,
            density: state[ 'density' ] as string | undefined,
            counter: state[ 'counter' ] != null ? String(state[ 'counter' ]) : undefined,
            role: state[ 'role' ] as 'user' | 'editor' | undefined,
            engineerName: state[ 'engineerName' ] as string | undefined,
            revision: state[ 'revision' ] as string | undefined,
            reportLanguage: state[ 'reportLanguage' ] as Language | undefined,
        };
        sessionStorage.setItem(SESSION_PDF_VIEWER_META_KEY, JSON.stringify(payload));
    } catch {
        // ignore
    }
}

export function readPdfViewerPersistedMeta(): PdfViewerPersistedMeta | null {
    try {
        const raw = sessionStorage.getItem(SESSION_PDF_VIEWER_META_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as PdfViewerPersistedMeta;
    } catch {
        return null;
    }
}

const EDITOR_WORK_DRAFT_KEY_PREFIX = 'qdEditorWorkDraftV1:';

/** Remove editor unsaved-work drafts from sessionStorage (all drawings in this tab). */
export function clearEditorWorkDraftKeysFromSession(): void {
    try {
        const toRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith(EDITOR_WORK_DRAFT_KEY_PREFIX)) {
                toRemove.push(k);
            }
        }
        toRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // ignore
    }
}

export type ResetPdfViewerSessionOptions = {
    /**
     * Keep operator home fields in localStorage (detail, order, last revision id per detail).
     * Use after a successful user-role report save so Home restores the same inputs.
     */
    preserveOperatorHomeInputs?: boolean;
};

/**
 * Call when leaving PdfViewer to Home (or equivalent): route meta, drafts, home form cache, embedded editor LS.
 * Does not clear UI language / report language / Firebase profile.
 */
export function resetPdfViewerAndHomeFormSession(options?: ResetPdfViewerSessionOptions): void {
    try {
        sessionStorage.removeItem(SESSION_PDF_VIEWER_META_KEY);
    } catch {
        // ignore
    }
    clearEditorWorkDraftKeysFromSession();
    try {
        localStorage.removeItem('editorExcelRects');
        localStorage.removeItem('editorExcelRefDimensions');
        if (!options?.preserveOperatorHomeInputs) {
            localStorage.removeItem('cachedDetail');
            localStorage.removeItem('cachedOrder');
            localStorage.removeItem('cachedOperatorRevisionByDetail');
        }
    } catch {
        // ignore
    }
}
